import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { hideModal } from '../../../actions/modal.actions';
import { getModalData } from "../../../selectors";
import DiagnosticIndicator, { DiagnosticStatus, mapStatus } from '../../../components/UI/DiagnosticIndicator';
import { getToken, getRefreshToken } from '../../../utils/helpers';
import { API_ROOT } from '../../../utils/api-config';

/**
 * Modal to display device status logs/details
 *
 * @param {object} modalProps.device - Device object
 * @param {object} modalProps.logs - Device logs data
 * @param {string} modalProps.status - Current device status
 * @param {object} modalProps.diagnosticResponse - Diagnostic configuration
 */
class EquipmentDeviceStatusLogsModal extends Component {

  state = {
    isLoading: false,
    currentStatus: DiagnosticStatus.UNKNOWN,
    currentLogs: null
  };

  componentDidMount() {
    const { logs, status } = this.props.modalProps || {};
    this.setState({
      currentStatus: status,
      currentLogs: logs
    });
  }

  runDiagnosticAction = async (actionKey, device, diagnosticResponse) => {
    this.setState({
      isLoading: true,
      currentStatus: DiagnosticStatus.LOADING,
      currentLogs: null
    });

    try {
      const payload = {
        deviceNums: device?.apiServiceDeviceNums || [],
        deviceProfile: diagnosticResponse?.deviceProfileName || "ONT",
        interfaces: [diagnosticResponse?.provisioningInterfaceName || "CALIX-SMX"]
      };

      const response = await fetch(
        `${API_ROOT}/provisioning/diagnostic/${actionKey}`,
        {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
            'X-API-Token': getToken(),
            'X-API-Refresh-Token': getRefreshToken()
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const actionResponseData = await response.json();

      if (actionResponseData && actionResponseData.state) {
        const newStatus = mapStatus(actionResponseData);

        let parsedLogs = actionResponseData.rawResponse;
        if (typeof parsedLogs === 'string') {
          try {
            const jsonString = parsedLogs.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false');
            parsedLogs = JSON.parse(jsonString);
          } catch (parseError) {
            console.warn('Failed to parse rawResponse string:', parseError);
            parsedLogs = { error: 'Failed to parse response', raw: parsedLogs };
          }
        }

        this.setState({
          currentStatus: newStatus,
          currentLogs: newStatus === DiagnosticStatus.UNKNOWN ? null : parsedLogs,
          isLoading: false
        });

        const { onUpdateParent } = this.props.modalProps || {};
        if (onUpdateParent) {
          onUpdateParent(newStatus, parsedLogs);
        }
      } else {
        const unknownStatus = DiagnosticStatus.UNKNOWN;

        this.setState({
          currentStatus: unknownStatus,
          isLoading: false
        });

        const { onUpdateParent } = this.props.modalProps || {};
        if (onUpdateParent) {
          onUpdateParent(unknownStatus, null);
        }
      }
    } catch (error) {
      const unknownStatus = DiagnosticStatus.UNKNOWN;

      this.setState({
        currentStatus: unknownStatus,
        isLoading: false
      });

      const { onUpdateParent } = this.props.modalProps || {};
      if (onUpdateParent) {
        onUpdateParent(unknownStatus, null);
      }
    }
  }

  renderLogsContent(logs) {
    if (!logs) return null;

    const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);
    const isArray = Array.isArray;
    const isPrimitive = (v) =>
      typeof v === "string" || typeof v === "number" || typeof v === "boolean";

    const formatKey = (key) => {
      return key
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const formatDate = (timestamp) => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          return timestamp;
        }
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } catch {
        return timestamp;
      }
    };

    const formatValue = (key, value) => {
      if (key.toLowerCase().includes('date') && typeof value === 'number') {
        return formatDate(value);
      }
      return value;
    };

    const Badge = ({ value, type = 'default' }) => {
      let style;

      if (value === true) {
        style = { bg: '#d4edda', color: '#155724', border: '#c3e6cb' };
      } else if (value === false) {
        style = { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' };
      } else {
        style = { bg: '#e2e3e5', color: '#383d41', border: '#d6d8db' };
      }

      return (
        <span style={{
          display: "inline-block",
          padding: "4px 8px",
          fontSize: "11px",
          fontWeight: "600",
          borderRadius: "8px",
          backgroundColor: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          whiteSpace: "nowrap"
        }}>
          {String(formatValue('', value))}
        </span>
      );
    };

    const CompactRow = ({ label, value, isLast = false }) => (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        borderBottom: isLast ? "none" : "1px solid #f1f3f4",
        backgroundColor: "#fafbfc"
      }}>
        <div style={{
          fontWeight: "500",
          color: "#374151",
          fontSize: "12px",
          flex: "0 0 auto",
          marginRight: "12px"
        }}>
          {label}
        </div>
        <div style={{
          flex: "1 1 auto",
          textAlign: "right",
          minWidth: 0
        }}>
          {isPrimitive(value) ? <Badge value={formatValue(label, value)} /> : String(value)}
        </div>
      </div>
    );

    const Card = ({ title, children, variant = 'default' }) => {
      const variants = {
        default: { border: '#e5e7eb', bg: '#ffffff', headerBg: '#f9fafb' },
        primary: { border: '#e5e7eb', bg: '#ffffff', headerBg: '#f9fafb' },
        success: { border: '#e5e7eb', bg: '#ffffff', headerBg: '#f9fafb' },
        warning: { border: '#e5e7eb', bg: '#ffffff', headerBg: '#f9fafb' },
        danger: { border: '#e5e7eb', bg: '#ffffff', headerBg: '#f9fafb' }
      };

      const style = variants[variant];

      return (
        <div style={{
          border: `1px solid ${style.border}`,
          borderRadius: "8px",
          backgroundColor: style.bg,
          marginBottom: "16px",
          overflow: "hidden"
        }}>
          {title && (
            <div style={{
              padding: "12px 16px",
              backgroundColor: style.headerBg,
              borderBottom: `1px solid ${style.border}`,
              fontWeight: "600",
              fontSize: "13px",
              color: "#374151"
            }}>
              {title}
            </div>
          )}
          <div style={{ padding: "0" }}>
            {children}
          </div>
        </div>
      );
    };

    const ObjectGrid = ({ items, title }) => (
      <Card title={title}>
        {items.map((item, idx) => {
          if (!isObject(item)) {
            return (
              <div key={idx} style={{ padding: "8px 16px" }}>
                <Badge value={item} />
              </div>
            );
          }

          const entries = Object.entries(item);
          return (
            <div key={idx} style={{
              padding: "16px",
              borderBottom: idx === items.length - 1 ? "none" : "1px solid #f1f3f4"
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
                alignItems: "start"
              }}>
                {entries.map(([k, v]) => (
                  <div key={k} style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    minWidth: 0
                  }}>
                    <div style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>
                      {formatKey(k)}
                    </div>
                    <div>
                      {isPrimitive(v) ? <Badge value={formatValue(k, v)} /> : String(v)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </Card>
    );

    const renderNode = (key, value, depth = 0) => {
      if (isPrimitive(value)) {
        return (
          <div key={key} style={{ marginBottom: "8px" }}>
            <CompactRow label={formatKey(key)} value={value} isLast={true} />
          </div>
        );
      }

      if (isArray(value)) {
        if (value.length > 0 && isObject(value[0])) {
          return (
            <div key={key} style={{ marginBottom: "16px" }}>
              <ObjectGrid items={value} title={formatKey(key)} />
            </div>
          );
        }

        return (
          <Card key={key} title={formatKey(key)}>
            <div style={{
              padding: "12px 16px",
              display: "flex",
              flexWrap: "wrap",
              gap: "6px"
            }}>
              {value.map((item, idx) => (
                <Badge key={idx} value={item} />
              ))}
            </div>
          </Card>
        );
      }

      if (isObject(value)) {
        const entries = Object.entries(value);

        let variant = 'default';
        const keyLower = key.toLowerCase();
        if (keyLower.includes('error') || keyLower.includes('fail')) variant = 'danger';
        else if (keyLower.includes('success') || keyLower.includes('online')) variant = 'success';
        else if (keyLower.includes('warning') || keyLower.includes('degraded')) variant = 'warning';
        else if (keyLower.includes('detail') || keyLower.includes('payload')) variant = 'primary';

        return (
          <Card key={key} title={formatKey(key)} variant={variant}>
            {entries.map(([k2, v2], idx) => {
              if (isPrimitive(v2)) {
                return (
                  <CompactRow
                    key={k2}
                    label={formatKey(k2)}
                    value={v2}
                    isLast={idx === entries.length - 1}
                  />
                );
              } else {
                return (
                  <div key={k2} style={{ padding: "8px 16px" }}>
                    {renderNode(k2, v2, depth + 1)}
                  </div>
                );
              }
            })}
          </Card>
        );
      }

      return null;
    };

    return (
      <div style={{ fontSize: "13px", lineHeight: 1.4 }}>
        {Object.entries(logs).map(([k, v]) => renderNode(k, v, 0))}
      </div>
    );
  }

  render() {
    const { device, diagnosticResponse, diagnostics } = this.props.modalProps || {};
    const { isLoading, currentStatus, currentLogs } = this.state;

    const logs = currentLogs;
    const status = currentStatus;

    const parseIcons = (response) => {
      const fallbackIcons = {
        loading: { icon: "fa fa-spinner fa-spin", color: "#1E90FF", label: "Checking..." },
        online: { icon: "fa fa-circle", color: "#008000", label: "Online" },
        degraded: { icon: "fa fa-exclamation-triangle", color: "#FFA500", label: "Degraded" },
        offline: { icon: "fa fa-circle", color: "#808080", label: "Offline" },
        unknown: { icon: "fa fa-question-circle", color: "#C0C0C0", label: "Unknown" },
        action: { icon: "fas fa-sync", color: "#1E90FF", label: "Run" }
      };

      try {
        if (response && response.icons) {
          const parsedIcons = JSON.parse(response.icons);
          return { ...fallbackIcons, ...parsedIcons };
        }
      } catch (err) {
        console.error("Failed to parse icons:", err);
      }
      return fallbackIcons;
    };

    const icons = parseIcons(diagnosticResponse);
    const statusIcon = icons[status] || icons.unknown;

    return (
      <Fragment>
        <div className="modal" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i
                    className={statusIcon.icon}
                    style={{ color: statusIcon.color, fontSize: '16px' }}
                    aria-hidden="true"
                  ></i>
                  Device Status Details - {statusIcon.label}
                </h5>
                <button onClick={this.props.hideModal} type="button" className="close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <div style={{ marginBottom: "20px", padding: "12px", backgroundColor: "#f8f9fa", borderRadius: "6px", position: "relative" }}>
                  {diagnostics && diagnostics.length > 0 && (
                    <DiagnosticIndicator
                      diagnosticResponse={diagnostics}
                      statusDiagnostic={diagnosticResponse}
                      isLoading={isLoading}
                      onRunAction={this.runDiagnosticAction}
                      onToggleLogs={() => {}}
                      device={device}
                      status={status}
                      logs={logs}
                      hasLogs={!!logs}
                      hideLogs={true}
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px"
                      }}
                    />
                  )}

                  <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", paddingRight: "60px" }}>
                    <span style={{ fontWeight: "600", fontSize: "14px" }}>
                      {device?.deviceItemDescription || device?.item?.name || 'Device'}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px" }}>
                    <strong>Description:</strong> {device?.item?.description || device?.deviceItemDescription || 'N/A'}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    <strong>Serial Number:</strong> {(() => {
                      const serialNumberObj = device?.apiServiceDeviceNums?.find(num =>
                        num.type === "Serial Number"
                      );
                      return serialNumberObj?.number || device?.serialNumber || 'N/A';
                    })()}
                  </div>
                </div>

                <div>
                  <h6 style={{ marginBottom: "12px", fontWeight: "600" }}>Status Details:</h6>
                  {logs && currentStatus !== DiagnosticStatus.UNKNOWN ? (
                    this.renderLogsContent(logs)
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6c757d"
                      }}
                    >
                      {currentStatus === DiagnosticStatus.UNKNOWN
                        ? "Unable to retrieve device status"
                        : "No status details available"}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={this.props.hideModal} className="btn btn-secondary" type="button">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" tabIndex="1" />
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const modalProps = getModalData(state).modalProps;

  return {
    modalProps
  };
};

const mapDispatchToProps = {
  hideModal
};

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentDeviceStatusLogsModal);
